/**
 * Cart attribution + Segment anonymous_id persistence
 * - Reads UTMs/ref/src from URL
 * - Reads src from localStorage fallback
 * - Reads Segment anonymousId (window.analytics first, then ajs_anonymous_id)
 * - Writes merge-safe cart attributes via /cart/update.js
 * - Patches fetch so any future /cart/update.js calls automatically merge required attrs
 */

(() => {
  // -----------------------------
  // Utilities
  // -----------------------------

  const isProd = () => window.shopSettings?.environment === 'prod';
  const log = (...args) => {
    if (!isProd()) console.log(...args);
  };

  const safeTrim = (v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s ? s : null;
  };

  const getUrlParam = (url, key) => safeTrim(url.searchParams.get(key));

  const getCleanExternalReferrer = () => {
    let saved = safeTrim(localStorage.getItem('shopify_referrer'));

    if (document.referrer && safeTrim(document.referrer)) {
      let host = location.hostname;
      let cleanedHost = host.replace(/^www\./, '');
      cleanedHost = cleanedHost.replace(/\.(com|org|net|io|co|biz|info|us|ca|uk|gov|edu)$/, '');

      // Store only if the referrer is not your own domain-ish
      if (!document.referrer.includes(cleanedHost)) {
        saved = document.referrer;
        localStorage.setItem('shopify_referrer', saved);
      }
    }

    return saved;
  };

  const getExistingCartAttributes = () => window.shopCart?.cartAttributes || {};

  const setCartAttributesMergeSafe = async (updates = {}) => {
    const existing = getExistingCartAttributes();
    const merged = { ...existing, ...updates };

    // Diff: only write if something actually changed
    const norm = (v) => (v === undefined || v === null ? '' : String(v));
    const keys = Object.keys(updates);
    const changed = keys.some((k) => norm(existing[k]) !== norm(merged[k]));
    if (!changed) return null;

    log('[cart] setCartAttributesMergeSafe', { existing, updates });

    const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: merged })
    });

    const data = await res.json();

    // Keep local cache in sync (best effort)
    if (!window.shopCart) window.shopCart = {};
    window.shopCart.cartAttributes = data?.attributes || merged;

    return data;
  };

  // -----------------------------
  // URL readers
  // -----------------------------

  function getUTMFromUrl() {
    const url = new URL(window.location.href);

    const utm_id = getUrlParam(url, 'utm_id');
    const utm_source = getUrlParam(url, 'utm_source');
    const utm_medium = getUrlParam(url, 'utm_medium');
    const utm_campaign = getUrlParam(url, 'utm_campaign');
    const utm_term = getUrlParam(url, 'utm_term');
    const utm_content = getUrlParam(url, 'utm_content');
    const ref = getUrlParam(url, 'ref');

    const params = {};
    let hasAny = false;

    if (utm_id)       { params.utm_id = utm_id; hasAny = true; }
    if (utm_source)   { params.utm_source = utm_source; hasAny = true; }
    if (utm_medium)   { params.utm_medium = utm_medium; hasAny = true; }
    if (utm_campaign) { params.utm_campaign = utm_campaign; hasAny = true; }
    if (utm_term)     { params.utm_term = utm_term; hasAny = true; }
    if (utm_content)  { params.utm_content = utm_content; hasAny = true; }

    // IMPORTANT: ref counts as present too
    if (ref)          { params.ref = ref; hasAny = true; }

    return hasAny ? params : null;
  }

  function getSrcFromUrl() {
    const url = new URL(window.location.href);

    // Prefer srcCode over src
    const srcCode = getUrlParam(url, 'srcCode');
    const src = getUrlParam(url, 'src');

    const value = srcCode || src;
    return value ? { source_code: value } : null;
  }

  // -----------------------------
  // Storage readers
  // -----------------------------

  function getSourceCodeFromStorage() {
    const srcCode = safeTrim(localStorage.getItem('srcCode'));
    const srcFallback = safeTrim(localStorage.getItem('src'));
    return srcCode || srcFallback || null;
  }

  // -----------------------------
  // Segment anonymous id
  // -----------------------------

  function getSegmentAnonymousId() {
    let anonymousID = null;

    try {
      if (window.analytics && typeof window.analytics.user === 'function') {
        const segUser = window.analytics.user();
        if (segUser && typeof segUser.anonymousId === 'function') {
          anonymousID = segUser.anonymousId();
        }
      }
    } catch (e) {
      console.warn('Error getting Segment anonymousId from window.analytics', e);
    }

    if (!anonymousID) {
      anonymousID = localStorage.getItem('ajs_anonymous_id');

      // Segment can store as a JSON string: "\"abc-123\""
      if (anonymousID && anonymousID.startsWith('"') && anonymousID.endsWith('"')) {
        anonymousID = anonymousID.slice(1, -1);
      }
    }

    return safeTrim(anonymousID);
  }

  // -----------------------------
  // Primary behaviors
  // -----------------------------

  async function saveUtmParameters() {
    const utmParams = getUTMFromUrl(); // may be null
    const srcParams = getSrcFromUrl(); // may be null

    // Optional: if URL doesn't have src but storage does, fill it
    const srcFromStorage = getSourceCodeFromStorage();
    const srcStorageParams = srcFromStorage ? { source_code: srcFromStorage } : null;

    if (!utmParams && !srcParams && !srcStorageParams) return;

    const analyticsSessionId = safeTrim(localStorage.getItem('analytics_session_id')) || '';
    const referrer = getCleanExternalReferrer();

    const updates = {
      ...(utmParams || {}),
      ...(srcParams || {}),
      ...(srcStorageParams || {}),
      ...(analyticsSessionId ? { analytics_session_id: analyticsSessionId } : {}),
      ...(referrer ? { referrer } : {})
    };

    log('[saveUtmParameters] updates', updates);

    try {
      await setCartAttributesMergeSafe(updates);
    } catch (e) {
      console.error('Failed to update cart attributes:', e);
    }
  }

  async function syncAnonymousIdToCart(cartAttributes) {
    const idFromSegment = getSegmentAnonymousId();
    if (!idFromSegment) return;

    const current = cartAttributes || getExistingCartAttributes();
    if (current && safeTrim(current.anonymous_id) === idFromSegment) return;

    if (window.__cartAnonymousIdSyncInProgress === idFromSegment) return;
    window.__cartAnonymousIdSyncInProgress = idFromSegment;

    try {
      await setCartAttributesMergeSafe({ anonymous_id: idFromSegment });
    } catch (e) {
      console.error('Failed to sync anonymous_id to cart:', e);
    } finally {
      window.__cartAnonymousIdSyncInProgress = null;
    }
  }

  function scheduleAnonymousIdSync(cartAttributes, attempt = 0) {
    const maxAttempts = 20; // more forgiving than 6; Segment can load slowly
    const retryDelay = 500;

    const id = getSegmentAnonymousId();
    if (id) {
      void syncAnonymousIdToCart(cartAttributes);
      return;
    }

    if (attempt < maxAttempts) {
      setTimeout(() => scheduleAnonymousIdSync(cartAttributes, attempt + 1), retryDelay);
    }
  }

  let anonymousIdSyncDebounceTimer = null;
  function triggerAnonymousIdSync(cartAttributes) {
    if (anonymousIdSyncDebounceTimer) clearTimeout(anonymousIdSyncDebounceTimer);
    anonymousIdSyncDebounceTimer = setTimeout(() => {
      scheduleAnonymousIdSync(cartAttributes);
    }, 150);
  }

  // Merge required attrs into outgoing cart updates
  function mergeRequiredAttributes(incomingAttributes = {}) {
    const base = getExistingCartAttributes();
    const merged = { ...base, ...incomingAttributes };

    const anonymousId = getSegmentAnonymousId();
    const sourceCode = getSourceCodeFromStorage();

    if (anonymousId && !safeTrim(merged.anonymous_id)) merged.anonymous_id = anonymousId;
    if (sourceCode && !safeTrim(merged.source_code)) merged.source_code = sourceCode;

    return merged;
  }

  function ensureJsonContentType(headers) {
    // Handle plain object or Headers
    if (!headers) return { 'Content-Type': 'application/json' };

    if (headers instanceof Headers) {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
      return headers;
    }

    if (typeof headers === 'object') {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      return headers;
    }

    return headers;
  }

  function patchCartUpdateFetch() {
    if (window.__patchedCartUpdateFetch) return;
    window.__patchedCartUpdateFetch = true;

    const originalFetch = window.fetch;

    window.fetch = function patchedFetch(input, init = {}) {
      // Allow internal calls to skip merging
      if (init.__skipCartMerge) {
        const { __skipCartMerge, ...rest } = init;
        return originalFetch.call(this, input, rest);
      }

      let url = '';
      if (typeof input === 'string') url = input;
      else if (input && typeof input.url === 'string') url = input.url;

      const isCartUpdate =
        typeof url === 'string' &&
        (url.includes('/cart/update.js') || url.includes('/cart/change.js'));

      const method = (init.method || 'GET').toUpperCase();
      const bodyIsString = typeof init.body === 'string';

      if (!isCartUpdate || method !== 'POST' || !bodyIsString) {
        return originalFetch.call(this, input, init);
      }

      let parsedBody;
      try {
        parsedBody = JSON.parse(init.body);
      } catch (e) {
        return originalFetch.call(this, input, init);
      }

      // If no attributes in the body, do nothing (avoid unexpected behavior)
      const hasAttributesField = parsedBody && Object.prototype.hasOwnProperty.call(parsedBody, 'attributes');
      if (!hasAttributesField) {
        return originalFetch.call(this, input, init);
      }

      const mergedAttributes = mergeRequiredAttributes(parsedBody.attributes || {});
      const nextBody = JSON.stringify({ ...parsedBody, attributes: mergedAttributes });

      const nextInit = {
        ...init,
        body: nextBody,
        headers: ensureJsonContentType(init.headers),
        __skipCartMerge: true
      };

      return originalFetch.call(this, input, nextInit);
    };
  }

  // -----------------------------
  // Boot
  // -----------------------------

  patchCartUpdateFetch();
  void saveUtmParameters();
  triggerAnonymousIdSync();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      triggerAnonymousIdSync(payload?.cartData?.attributes);
    });
  }
})();
